import {
  type AnyBlock,
  defaultFilterConfig,
  type FilterConfig,
  type FilterProvider,
  FilterQuery,
  type MutableFilterBlock,
} from '@github-ui/filter'
import {FilterQueryParser} from '@github-ui/filter/parser'
import {NestedFilterProvider} from '@github-ui/filter/providers'
import {isNestedFilterProvider} from '@github-ui/filter/utils'
import {useCallback, useMemo} from 'react'

import {
  cleanDisplayName as cleanProviderDisplayName,
  cleanFieldProviderKey,
  extractOrgsFromRawQuery,
  isFieldProviderKey,
  providerMatchesOrgContext,
} from './org-aware-utils'

interface UseOrgAwareProvidersOptions {
  baseFilterProviders: Array<FilterProvider | NestedFilterProvider>
  effectiveQuery: string
  issueFieldsGlobalSearchEnabled: boolean
  providerRepositoryScope?: string
}

/**
 * Checks if the providers contain any issue field providers that would benefit from org-aware processing
 */
function hasIssueFieldProviders(providers: Array<FilterProvider | NestedFilterProvider>): boolean {
  return providers.some(provider => {
    if (!isNestedFilterProvider(provider) || !provider.filterProviders) {
      return false
    }

    return provider.filterProviders.some(subProvider => subProvider.key && isFieldProviderKey(subProvider.key))
  })
}

// Converts provider array to record, handling both regular and nested providers
function flattenProvidersToRecord(
  providers: Array<FilterProvider | NestedFilterProvider>,
): Record<string, FilterProvider> {
  const providersRecord: Record<string, FilterProvider> = {}

  for (const provider of providers) {
    if (isNestedFilterProvider(provider)) {
      if (provider.filterProviders) {
        for (const subProvider of provider.filterProviders) {
          const key = subProvider.key
          if (key) {
            providersRecord[key] = subProvider
          }
        }
      }
    } else {
      // Handle regular FilterProvider
      const key = provider.key
      if (key) {
        providersRecord[key] = provider
      }
    }
  }

  return providersRecord
}

// Creates a cleaned provider with modified display name and key
function createCleanedProvider(
  originalProvider: FilterProvider,
  cleanDisplayName: string,
  cleanKey: string,
): FilterProvider {
  // When cleaning a provider key, adjust aliases to prevent duplicate suggestions
  const adjustedAliases = originalProvider.aliases?.filter(alias => alias !== cleanKey) || []

  const cleanedProvider = {
    ...originalProvider,
    displayName: cleanDisplayName,
    key: cleanKey,
    aliases: adjustedAliases,
    // Ensure all methods are properly bound
    getSuggestions: originalProvider.getSuggestions.bind(originalProvider),
    validateFilterBlockValues: originalProvider.validateFilterBlockValues.bind(originalProvider),
    getValueRowProps: originalProvider.getValueRowProps.bind(originalProvider),
  }

  // Override getSuggestions to use cleaned key and displayName
  if ('getSuggestions' in originalProvider && typeof originalProvider.getSuggestions === 'function') {
    const originalGetSuggestions = originalProvider.getSuggestions.bind(originalProvider)

    cleanedProvider.getSuggestions = async function (
      filterQuery: FilterQuery,
      filterBlock: AnyBlock | MutableFilterBlock,
      config: FilterConfig,
      caretIndex?: number | null,
    ) {
      // Temporarily override the original provider's properties
      const savedDisplayName = originalProvider.displayName
      const savedKey = originalProvider.key
      const savedAliases = originalProvider.aliases

      // Set cleaned values on the original provider (cast to mutable for temporary override)
      const mutableProvider = originalProvider as {displayName?: string; key: string; aliases?: string[]}
      mutableProvider.displayName = cleanDisplayName || ''
      mutableProvider.key = cleanKey
      mutableProvider.aliases = adjustedAliases

      try {
        // Create a mock filter block with the cleaned provider key
        if ('provider' in filterBlock && filterBlock.provider) {
          const mockFilterBlock = {
            ...filterBlock,
            provider: {...filterBlock.provider, key: cleanKey},
          } as typeof filterBlock

          return await originalGetSuggestions(filterQuery, mockFilterBlock, config, caretIndex)
        }

        return await originalGetSuggestions(filterQuery, filterBlock, config, caretIndex)
      } finally {
        // Restore original values
        mutableProvider.displayName = savedDisplayName
        mutableProvider.key = savedKey
        mutableProvider.aliases = savedAliases
      }
    }
  }

  return cleanedProvider
}

/**
 * Hook for processing filter providers with org-aware logic.
 *
 * This hook optimizes provider processing by:
 * - Debouncing query parsing to reduce processing frequency
 * - Caching parser instance to avoid recreation
 * - Only processing providers when org/repo context actually changes
 * - Early exit when no org context is needed
 *
 * @param options Configuration options for org-aware provider processing
 * @returns Processed providers filtered based on org/repo context
 */
export function useOrgAwareProviders({
  baseFilterProviders,
  effectiveQuery,
  issueFieldsGlobalSearchEnabled,
  providerRepositoryScope,
}: UseOrgAwareProvidersOptions): Array<FilterProvider | NestedFilterProvider> {
  // Check if we have issue field providers that would benefit from org-aware processing
  const hasFieldProviders = useMemo(() => {
    return issueFieldsGlobalSearchEnabled && hasIssueFieldProviders(baseFilterProviders)
  }, [issueFieldsGlobalSearchEnabled, baseFilterProviders])

  // Cache parser instance to avoid recreation
  const parser = useMemo(
    () =>
      new FilterQueryParser(baseFilterProviders.filter(p => !isNestedFilterProvider(p)) as FilterProvider[], {
        ...defaultFilterConfig,
        groupAndKeywordSupport: true,
      }),
    [baseFilterProviders],
  )

  // Extract org/repo context separately and only recompute when it actually changes
  const orgRepoContext = useMemo(() => {
    if (!hasFieldProviders) {
      return {hasContext: false, orgNames: new Set<string>(), contextKey: 'none'}
    }

    if (!effectiveQuery) {
      return {hasContext: false, orgNames: new Set<string>(), contextKey: 'none'}
    }

    try {
      // Use the shared utility to extract orgs directly from raw query instead of relying on parser context
      const orgsFromQuery = extractOrgsFromRawQuery(effectiveQuery)
      const orgNames = new Set(orgsFromQuery)

      const contextParts: string[] = []
      if (orgsFromQuery.length > 0) {
        contextParts.push(`org:${orgsFromQuery.join(',')}`)
      }

      const contextKey = contextParts.length > 0 ? contextParts.join('|') : 'none'
      const hasContext = orgNames.size > 0

      return {hasContext, orgNames, contextKey}
    } catch {
      return {hasContext: false, orgNames: new Set<string>(), contextKey: 'none'}
    }
  }, [hasFieldProviders, effectiveQuery])

  // Optimized provider processing function with cached org names
  const processOrgAwareProvidersWithCache = useCallback(
    (
      queryy: FilterQuery,
      filterProviderss: Record<string, FilterProvider>,
      cachedOrgNames: Set<string>,
    ): Record<string, FilterProvider> => {
      // Early exit if no org filtering needed
      if (cachedOrgNames.size === 0) {
        return filterProviderss
      }

      // Filter field providers based on org context
      const contextualFilterProviders = {...filterProviderss}

      // Filter and clean field providers based on org context
      for (const [key, provider] of Object.entries(filterProviderss)) {
        if (isFieldProviderKey(key)) {
          // Check if this field provider matches any org in context
          const matchesOrg = providerMatchesOrgContext(key, cachedOrgNames)
          if (!matchesOrg) {
            delete contextualFilterProviders[key]
          } else {
            // Clean display name and key using shared utilities
            const cleanedDisplayName = cleanProviderDisplayName(provider.displayName)
            const cleanedKey = cleanFieldProviderKey(provider.key, cachedOrgNames)

            // Only create wrapper if something actually changed
            if (cleanedDisplayName !== provider.displayName || cleanedKey !== provider.key) {
              contextualFilterProviders[key] = createCleanedProvider(provider, cleanedDisplayName, cleanedKey)
            }
          }
        }
      }

      return contextualFilterProviders
    },
    [],
  )

  // Process providers only when we have field providers and org/repo context actually changes
  const processedProviders = useMemo(() => {
    // Early exit if feature flag is disabled or no field providers exist
    if (!hasFieldProviders) {
      return baseFilterProviders
    }

    // Early exit if no org context - just flatten nested providers
    if (!orgRepoContext.hasContext) {
      return Object.values(flattenProvidersToRecord(baseFilterProviders))
    }

    // Convert array to record for processing
    const providersRecord = flattenProvidersToRecord(baseFilterProviders)

    // Create query context with cached parser (only for context injection)
    let queryContext: FilterQuery
    try {
      queryContext = parser.parse(effectiveQuery || '')
    } catch {
      queryContext = new FilterQuery(effectiveQuery || '', [], undefined, undefined, undefined, false)
    }

    // Add repository scope context if available and not already present
    if (providerRepositoryScope && !queryContext.context.repo) {
      const [owner] = providerRepositoryScope.split('/')
      Object.defineProperty(queryContext, 'staticContext', {
        value: {
          repo: providerRepositoryScope,
          org: owner,
        },
        writable: true,
      })
    }

    // Process with org-aware logic using cached org names
    const processedRecord = processOrgAwareProvidersWithCache(queryContext, providersRecord, orgRepoContext.orgNames)

    // Convert back to array
    return Object.values(processedRecord)
  }, [
    baseFilterProviders,
    hasFieldProviders,
    orgRepoContext.hasContext,
    orgRepoContext.orgNames,
    providerRepositoryScope,
    parser,
    processOrgAwareProvidersWithCache,
    effectiveQuery,
  ])

  return processedProviders
}
